import { useContext, useEffect, useRef, useState } from "react";
import { Empty, Modal, Space, Table, Tag } from "antd";
import { HeartOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { formatDateStringToDateAndTime, poeValorComPontosEmMil } from "../../../../utils/Conversores";
import { IAppUser, ISocialInstitution } from "../../../../interfaces";
import { formatCpf, formatPhone } from "../../../../utils/Masks";
import SocialInstitutionService from "../../../../services/http/services/SocialInstitutionService";
import { EmitError } from "../../../../utils/EmitError";
import { useGetColumnSearchProps } from "../../../../hooks/FilterTable/stringClearFiltros";
import { useGetColumnSearchPropsData } from "../../../../hooks/FilterTable/date";
import TableActionButton from "../../../../components/TableActionButton";
import { ThemeContext } from "../../../../contexts/ThemeContext";

interface IProps {
    open: boolean;
    socialInstitutionSelected: ISocialInstitution;
    onClose: () => void;
}

export default function ModalAppUsers({
    open,
    socialInstitutionSelected,
    onClose,
}: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const navigate = useNavigate();
    const [loading, setIsLoading] = useState<boolean>(false);
    const [tableData, setTableData] = useState<IAppUser[]>([]);
    const refFuncaoLimparFiltrosString = useRef<any>(null);

    useEffect(() => {
        if (!open) return;

        const controller = new AbortController();

        async function fetchAppUsers() {
            try {
                setIsLoading(true);

                const res = await SocialInstitutionService.listAppUsers({
                    socialInstitutionId: socialInstitutionSelected.id,
                    signal: controller.signal,
                });

                setTableData(res);
            } catch (err) {
                if (!(err instanceof AxiosError && err.name === 'CanceledError')) {
                    EmitError(err, true, 'appUsers_loadAppUsers');
                }
            } finally {
                setIsLoading(false);
            }
        }

        fetchAppUsers();

        return () => controller.abort();
    }, [open, socialInstitutionSelected.id]);

    const columns = [
        {
            title: 'Nome',
            dataIndex: 'name',
            key: 'name',
            sorter: (a: IAppUser, b: IAppUser) => a.name?.localeCompare(b.name || '') || 0,
            showSorterTooltip: false,
            ...useGetColumnSearchProps({ dataIndex: 'name', ref: refFuncaoLimparFiltrosString }),
            render: (name: string) => (
                <span style={{ fontWeight: 600, color: isDark ? '#f0f0f0' : '#1e293b' }}>
                    {name || '--'}
                </span>
            ),
        },
        {
            title: 'CPF',
            dataIndex: 'cpf',
            key: 'cpf',
            width: 140,
            ...useGetColumnSearchProps({ dataIndex: 'cpf', ref: refFuncaoLimparFiltrosString, formatter: formatCpf }),
            render: (cpf: string) => (
                <span style={{ fontFamily: 'monospace' }}>
                    {cpf ? formatCpf(cpf) : '--'}
                </span>
            ),
        },
        {
            title: 'Telefone',
            dataIndex: 'phone',
            key: 'phone',
            width: 150,
            ...useGetColumnSearchProps({ dataIndex: 'phone', ref: refFuncaoLimparFiltrosString, formatter: formatPhone }),
            render: (phone: string) => (
                <span>{phone ? formatPhone(phone) : '--'}</span>
            ),
        },
        {
            title: 'Data de Cadastro',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 160,
            sorter: (a: IAppUser, b: IAppUser) => new Date(a.created_at || '').getTime() - new Date(b.created_at || '').getTime(),
            showSorterTooltip: false,
            ...useGetColumnSearchPropsData({ dataIndex: 'created_at' }),
            render: (criado: any) => (
                <span style={{ color: isDark ? '#8c8c8c' : '#64748b' }}>
                    {criado ? formatDateStringToDateAndTime(criado, true) : '--'}
                </span>
            ),
        },
        {
            title: 'Ações',
            key: 'actions',
            width: 80,
            align: 'center' as const,
            fixed: 'right' as const,
            render: (_: any, record: IAppUser) => (
                <TableActionButton
                    action="view"
                    tooltip="Ver cadastro do cidadão"
                    onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                        navigate('/usuarios/aplicativo/visualizar/' + record.id);
                    }}
                />
            ),
        },
    ];

    return (
        <Modal
            width={1000}
            open={open}
            footer={null}
            onCancel={onClose}
            centered
            destroyOnClose
            title={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 24 }}>
                    <Space size={10} align="center">
                        <div
                            style={{
                                width: 36,
                                height: 36,
                                borderRadius: 8,
                                background: isDark ? 'rgba(235, 47, 150, 0.15)' : '#fff0f6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <HeartOutlined style={{ color: '#eb2f96', fontSize: 18 }} />
                        </div>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>
                                {socialInstitutionSelected.name}
                            </div>
                            <div style={{ fontSize: 12, color: isDark ? '#8c8c8c' : '#64748b', marginTop: 2 }}>
                                Usuários vinculados a esta instituição social
                            </div>
                        </div>
                    </Space>
                    <Tag color="pink" style={{ borderRadius: 6, fontWeight: 600, fontSize: 12, padding: '2px 8px' }}>
                        {poeValorComPontosEmMil(tableData.length)} {tableData.length === 1 ? 'apoiador' : 'apoiadores'}
                    </Tag>
                </div>
            }
            bodyStyle={{ padding: '12px 0 0 0' }}
        >
            <Table
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={tableData}
                className={`modern-data-table ${isDark ? 'modern-data-table-dark' : ''}`}
                scroll={{ x: 800 }}
                onRow={(record) => ({
                    onClick: () => {
                        onClose();
                        navigate('/usuarios/aplicativo/visualizar/' + record.id);
                    },
                    style: { cursor: 'pointer' },
                })}
                locale={{
                    emptyText: (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <span style={{ color: isDark ? '#8c8c8c' : '#64748b' }}>
                                    Nenhum usuário vinculado a esta instituição
                                </span>
                            }
                        />
                    ),
                }}
                pagination={{
                    pageSize: 8,
                    showSizeChanger: true,
                    pageSizeOptions: ['8', '15', '30'],
                    size: 'small',
                }}
            />
        </Modal>
    );
}
